import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreHorizontal, 
  Pencil, 
  Trash2,
  Euro,
  Calendar,
  User,
  GripVertical,
  MapPin
} from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const DEFAULT_STAGES = [
  { id: 'lead', name: 'Lead', color: '#94a3b8' },
  { id: 'qualificado', name: 'Qualificado', color: '#3b82f6' },
  { id: 'proposta', name: 'Proposta', color: '#f59e0b' },
  { id: 'negociacao', name: 'Negociação', color: '#8b5cf6' },
  { id: 'ganho', name: 'Ganho', color: '#10b981' },
  { id: 'perdido', name: 'Perdido', color: '#ef4444' }
];

export default function OpportunitiesPage() {
  const { api } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOpp, setEditingOpp] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  
  const [form, setForm] = useState({
    title: '',
    value: '',
    stage: 'lead',
    contact_id: '',
    contact_name: '',
    probability: 10,
    expected_close_date: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [oppsRes, contactsRes, pipelinesRes] = await Promise.all([
        api().get('/opportunities'),
        api().get('/contacts'),
        api().get('/pipelines')
      ]);
      setOpportunities(oppsRes.data);
      setContacts(contactsRes.data);
      if (pipelinesRes.data[0]?.stages) {
        setStages(pipelinesRes.data[0].stages);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...form,
      value: parseFloat(form.value) || 0
    };
    
    try {
      if (editingOpp) {
        await api().put(`/opportunities/${editingOpp.id}`, data);
        toast.success('Oportunidade atualizada!');
      } else {
        await api().post('/opportunities', data);
        toast.success('Oportunidade criada!');
      }
      
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao guardar oportunidade');
    }
  };

  const handleDelete = async (opp) => {
    if (!window.confirm(`Eliminar oportunidade "${opp.title}"?`)) return;
    
    try {
      await api().delete(`/opportunities/${opp.id}`);
      toast.success('Oportunidade eliminada!');
      loadData();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const handleEdit = (opp) => {
    setEditingOpp(opp);
    setForm({
      title: opp.title || '',
      value: opp.value?.toString() || '',
      stage: opp.stage || 'lead',
      contact_id: opp.contact_id || '',
      contact_name: opp.contact_name || '',
      probability: opp.probability || 10,
      expected_close_date: opp.expected_close_date || '',
      notes: opp.notes || ''
    });
    setShowModal(true);
  };

  const handleDragStart = (e, opp) => {
    setDraggedItem(opp);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.stage === stageId) return;
    
    try {
      await api().patch(`/opportunities/${draggedItem.id}/stage`, { stage: stageId });
      loadData();
      toast.success('Etapa atualizada!');
    } catch (error) {
      toast.error('Erro ao atualizar etapa');
    }
    setDraggedItem(null);
  };

  const resetForm = () => {
    setForm({
      title: '',
      value: '',
      stage: 'lead',
      contact_id: '',
      contact_name: '',
      probability: 10,
      expected_close_date: '',
      notes: ''
    });
    setEditingOpp(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getStageOpportunities = (stageId) => {
    return opportunities.filter(opp => opp.stage === stageId);
  };

  const getStageTotal = (stageId) => {
    return getStageOpportunities(stageId).reduce((sum, opp) => sum + (opp.value || 0), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="opportunities-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            {opportunities.length} oportunidades • Total: {formatCurrency(opportunities.reduce((s, o) => s + (o.value || 0), 0))}
          </p>
        </div>
        <Button 
          className="bg-indigo-800 hover:bg-indigo-900"
          onClick={() => { resetForm(); setShowModal(true); }}
          data-testid="new-opportunity-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Oportunidade
        </Button>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => (
          <div
            key={stage.id}
            className="flex-shrink-0 w-80"
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.id)}
            data-testid={`stage-column-${stage.id}`}
          >
            <Card className="h-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: stage.color }}
                    />
                    <CardTitle className="text-sm font-semibold font-[Manrope]">
                      {stage.name}
                    </CardTitle>
                    <Badge variant="secondary" className="text-xs">
                      {getStageOpportunities(stage.id).length}
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {formatCurrency(getStageTotal(stage.id))}
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <ScrollArea className="h-[calc(100vh-320px)]">
                  <div className="space-y-3 pr-2">
                    {getStageOpportunities(stage.id).map((opp) => (
                      <div
                        key={opp.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, opp)}
                        className={`
                          p-4 bg-white border rounded-lg cursor-grab active:cursor-grabbing
                          hover:shadow-md hover:-translate-y-0.5 transition-all duration-200
                          ${draggedItem?.id === opp.id ? 'opacity-50' : ''}
                        `}
                        data-testid={`opportunity-card-${opp.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-medium text-sm line-clamp-2">{opp.title}</h4>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 flex-shrink-0"
                            onClick={() => handleEdit(opp)}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        </div>
                        
                        <p className="text-lg font-bold text-indigo-800 tabular-nums mb-2">
                          {formatCurrency(opp.value)}
                        </p>
                        
                        {opp.contact_name && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                            <User className="h-3 w-3" />
                            {opp.contact_name}
                          </div>
                        )}
                        
                        {opp.expected_close_date && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(opp.expected_close_date), "d MMM yyyy", { locale: pt })}
                          </div>
                        )}
                        
                        <div className="mt-3 flex items-center justify-between">
                          <Badge variant="outline" className="text-xs">
                            {opp.probability}% prob.
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-red-500 hover:text-red-600"
                            onClick={() => handleDelete(opp)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {getStageOpportunities(stage.id).length === 0 && (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        Arraste oportunidades para aqui
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editingOpp ? 'Editar Oportunidade' : 'Nova Oportunidade'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  data-testid="opp-title-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="value">Valor (€)</Label>
                  <Input
                    id="value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: e.target.value })}
                    data-testid="opp-value-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="probability">Probabilidade (%)</Label>
                  <Input
                    id="probability"
                    type="number"
                    min="0"
                    max="100"
                    value={form.probability}
                    onChange={(e) => setForm({ ...form, probability: parseInt(e.target.value) || 0 })}
                    data-testid="opp-probability-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="stage">Etapa</Label>
                  <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                    <SelectTrigger data-testid="opp-stage-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {stages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                            {stage.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expected_close_date">Data Fecho Esperada</Label>
                  <Input
                    id="expected_close_date"
                    type="date"
                    value={form.expected_close_date}
                    onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })}
                    data-testid="opp-date-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contact">Contacto</Label>
                <Select 
                  value={form.contact_id} 
                  onValueChange={(v) => {
                    const contact = contacts.find(c => c.id === v);
                    setForm({ ...form, contact_id: v, contact_name: contact?.name || '' });
                  }}
                >
                  <SelectTrigger data-testid="opp-contact-select">
                    <SelectValue placeholder="Selecionar contacto" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name} {contact.company && `(${contact.company})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="opp-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-indigo-800 hover:bg-indigo-900" data-testid="save-opp-btn">
                {editingOpp ? 'Guardar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
